import React, { useState, useEffect } from 'react';
import posthog from 'posthog-js';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import './FAQ.css';

interface FAQSection {
  id: string;
  title: string;
  subsections: FAQSubsection[];
}

interface FAQSubsection {
  id: string;
  title: string;
  content: string;
  platformContent?: {
    mac?: InstallationContent;
    windows?: InstallationContent;
    universal?: InstallationContent;
  };
}

interface InstallationContent {
  video?: string;
  steps: InstallationStep[];
}

interface InstallationStep {
  number: number;
  title: string;
  description: string;
  image?: string;
}

const faqSections: FAQSection[] = [
  {
    id: 'installation',
    title: 'Installation',
    subsections: [
      {
        id: 'how-to-install',
        title: 'How to Install',
        content: 'Installing ColorCache is simple and works the same way on both Mac and Windows!',
        platformContent: {
          universal: {
            steps: [
              {
                number: 1,
                title: 'Install via Adobe Exchange',
                description: 'Visit the <a href="https://exchange.adobe.com/apps/cc/203997/colorcache" target="_blank" rel="noopener noreferrer">Adobe Exchange page</a> and hit "Install" or "Free" to add ColorCache to your Creative Cloud account.',
              },
              {
                number: 2,
                title: 'Open ColorCache in Premiere Pro',
                description: 'Open Premiere Pro, then go to <strong>Window &gt; Extensions &gt; ColorCache</strong>.',
              },
              {
                number: 3,
                title: 'Start Your 7-Day Free Trial',
                description: 'Upon opening the extension, you can start your 7-day free trial immediately with full access to all features — no credit card required!',
              },
              {
                number: 4,
                title: 'Get a License Key',
                description: 'After your 7-day trial, or at any time you choose, you can upgrade to ColorCache with a license key to ensure uninterrupted access. <a href="/checkout">Buy a license here</a>.',
              }
            ]
          }
        }
      },
      {
        id: 'compatibility',
        title: 'Compatibility',
        content: 'ColorCache is compatible with Premiere Pro 24 through 25. It works on both Mac and Windows!'
      },
      {
        id: 'how-to-login',
        title: 'How to Activate',
        content: 'If you purchase a license, you will receive an email with a license key. Enter your license key and the email address you used for purchase inside the ColorCache extension to activate your access.'
      }
    ]
  },
  {
    id: 'features',
    title: 'Features',
    subsections: [
      {
        id: 'adding-palettes',
        title: 'Adding Palettes',
        content: 'You can create multiple palettes to organize your colors by brand, project, or any other system that works for you. This helps keep your colors organized and easy to find. To create a new palette, click the "New Palette" button in ColorCache.'
      },
      {
        id: 'renaming-palettes',
        title: 'Renaming Palettes',
        content: 'You can rename a palette by double clicking on the palette name in the palette list and typing the new name.'
      },
      {
        id: 'adding-colors',
        title: 'Adding Colors',
        content: 'Once you\'ve created a new palette, click the plus button and enter the HEX code or RGB values. You can also use the color picker to select colors visually.'
      },
      {
        id: 'copying-colors',
        title: 'Copying Colors',
        content: 'Right click any color in ColorCache and copy the HEX values. You can then paste the HEX values into the color field of the element you want to color in Premiere Pro, such as a shape or a text element.'
      },
      {
        id: 'deleting-colors',
        title: 'Deleting Colors',
        content: 'To delete a color, right-click on it and select "Delete" from the context menu.'
      },
      {
        id: 'importing-exporting',
        title: 'Importing & Exporting Palettes',
        content: 'You can easily share your brand assets with your team. To export an individual palette, click the three dots (...) next to the palette name and select \"Export Palette\" to generate a ZIP file. For sharing all your palettes at once, click \"Bulk Share Palettes\" in the footer to generate individual ZIP files for every palette in your collection. To import, simply click \"Import Palette(s)\" in the footer and select the ZIP file(s) you wish to add.'
      }
    ]
  },
  {
    id: 'updates',
    title: 'Updates',
    subsections: [
      {
        id: 'how-to-update',
        title: 'How To Update',
        content: 'We\'ll notify you when updates are available inside ColorCache. To update, simply download the latest version via the update button in the extension. Your color data will be preserved.'
      }
    ]
  },
  {
    id: 'pricing',
    title: 'Pricing & Licensing',
    subsections: [
      {
        id: 'free-access',
        title: '7-Day Free Trial',
        content: 'You can download and install ColorCache for free via the Adobe Exchange. Every new installation includes 7 days of full access. No credit card is required to start your trial — just install and start using it!'
      },
      {
        id: 'license-terms',
        title: 'License Terms',
        content: 'An annual subscription ($9.99/year) allows you to use ColorCache on a single device. Your purchase includes access to all features and updates while your subscription is active.'
      },
      {
        id: 'refund-policy',
        title: 'Refund Policy',
        content: 'Since you get a 7-day free trial of ColorCache, we encourage you to test it out before upgrading. While all sales are generally final, we may issue refunds at our own discretion in specific cases.'
      }
    ]
  },
  {
    id: 'contact',
    title: 'Contact',
    subsections: [
      {
        id: 'report-bug',
        title: 'Report a Bug',
        content: 'Found an issue? Let us know and we\'ll fix it! <a href="/bugreport">Submit a Bug Report</a>'
      },
      {
        id: 'contact-us',
        title: 'Contact Us',
        content: 'Can\'t find the answer you\'re looking for? Our support team is here to help. Email us at support@codec422.com.'
      }
    ]
  }
];

const FAQ: React.FC = () => {
  const [selectedSubsection, setSelectedSubsection] = useState<string>('how-to-install');

  const currentSubsection = faqSections
    .flatMap(section => section.subsections)
    .find(sub => sub.id === selectedSubsection);

  const handleSubsectionClick = (subsectionId: string) => {
    setSelectedSubsection(subsectionId);
    posthog.capture('faq_subsection_viewed', { subsection_id: subsectionId });
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="faq-page">
      <Header showNav={true} />
      {/* FAQ Hero Section */}
      <section className="faq-hero">
        <div className="container">
          <div className="faq-hero-content">
            <h1>Need help? We've got you covered.</h1>
            <p>Find all you need to know about ColorCache here.</p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="faq-content">
        <div className="container">
          <div className="faq-layout">
            {/* Left Sidebar */}
            <div className="faq-sidebar">
              <nav className="faq-nav">
                {faqSections.map(section => (
                  <div key={section.id} className="nav-section">
                    <div className="nav-section-header">
                      {section.title}
                    </div>
                    <div className="nav-subsections">
                      {section.subsections.map(subsection => (
                        <button
                          key={subsection.id}
                          className={`nav-subsection-btn ${selectedSubsection === subsection.id ? 'active' : ''}`}
                          onClick={() => handleSubsectionClick(subsection.id)}
                        >
                          {subsection.title}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </nav>
            </div>

            {/* Right Content Area */}
            <div className="faq-main-content">
              {currentSubsection && (
                <div className="content-section">
                  <h2>{currentSubsection.title}</h2>

                  {currentSubsection.platformContent ? (
                    <div className="installation-content">
                      <div className="content-text">
                        {currentSubsection.content.split('\n').map((paragraph, index) => (
                          <p key={index} dangerouslySetInnerHTML={{ __html: paragraph }}></p>
                        ))}
                      </div>

                      {/* Installation Steps */}
                      <div className="installation-steps">
                        <h3>Step-by-Step Installation</h3>
                        {(currentSubsection.platformContent.universal || currentSubsection.platformContent.mac || currentSubsection.platformContent.windows)?.steps.map((step) => (
                          <div key={step.number} className="installation-step">
                            <div className="step-header">
                              <div className="step-number">{step.number}</div>
                              <h4>{step.title}</h4>
                            </div>
                            <div className="step-content">
                              <p dangerouslySetInnerHTML={{ __html: step.description }}></p>
                              {step.image && (
                                <div className="step-image">
                                  <img src={step.image} alt={`Step ${step.number}: ${step.title}`} />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="content-text">
                      {currentSubsection.content.split('\n').map((paragraph, index) => (
                        <p key={index} dangerouslySetInnerHTML={{ __html: paragraph }}></p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ; 